/**
 * AVANCEMENT client d'une récupération Enedis — la phrase qui répond « où j'en
 * suis ? » sous la bannière (décisions A1-A4 du 25/08, SSOT DEV-1499, gravées au
 * contrat `enedis-2026/docs/conception/06-contrat-livraison-client-final.md`
 * §8 bis). Logique PURE, extraite du composable pour être testable.
 *
 * ## Trois objets à ne JAMAIS confondre (A2)
 *
 * 1. la **FRAÎCHEUR** — de quand date la dernière mesure reçue
 *    (`last_data_date`, `lag_days`) ;
 * 2. le **STOCK** — ce qu'on détient, `date_from` → `date_to` (c'est l'onglet
 *    « Données reçues », pas la bannière) ;
 * 3. la **DURÉE DE MISE À DISPOSITION** — du clic « mettre à jour » jusqu'à ce
 *    que la donnée soit dans l'app.
 *
 * Ce module ne produit QUE des phrases sur (1) et (3). Il n'affirme jamais rien
 * sur (2) : le stock a son propre écran.
 *
 * ## L'échéance est GELÉE À L'ÉMISSION (A1)
 *
 * Aucune des deux échéances calculées ici ne dépend de `maintenant` : l'ETA de
 * mise à disposition ne dépend que de `emisLe`, l'échéance de la prochaine
 * mesure que de (`last_data_date`, `expected_lag_days`). `maintenant` ne sert
 * qu'à savoir si l'échéance est DÉPASSÉE. C'est la garde de la décision A1 :
 * une échéance recalculée à chaque affichage recule devant le retard, et le
 * client ne voit jamais qu'il y a un problème.
 *
 * ## Aucun seuil plat côté front
 *
 * L'attendu dépend du compteur (segment × mécanisme : J+1 sur un flux quotidien,
 * J+3 sur une commande M023, ~35 j sur un index mensuel). Il est SERVI par
 * `verdict.expected_lag_days` (api-metier `traceDuVerdict`). On ne fabrique
 * aucune constante de jours ici : un champ absent ⇒ **pas de phrase**, jamais
 * une valeur de repli.
 *
 * ### Couverture RÉELLE de `expected_lag_days` — mesurée, pas supposée
 *
 * Sondage en lecture seule du **31/08/2026 ~17 h 50 UTC**, `POST /status/sources`
 * (service enedis, la route même que consomme api-metier), sur **203 PRM
 * distincts** — page 1 de la recherche flotte `pdl` (200) + 13 témoins nommés :
 *
 * - `expected_lag_days` **renseigné : 19/203 (9,4 %)** ; absent : 184/203 ;
 * - restreint aux PRM porteurs d'un `last_data_date` — les SEULS qui peuvent
 *   produire `avancement_prochaine_mesure` — : **16 PRM, dont 1 SANS attendu**
 *   (`30001911507698` : `last_data_date: "2026-08-21"`, `lag_days: 10`,
 *   `expected_lag_days: null`). Soit **1 trou sur 16** là où ça compte ;
 * - **toutes** les valeurs non nulles observées valent `3` — aucune autre valeur
 *   sur l'échantillon — et `mode` vaut `null` sur 203/203.
 *
 * ⚠️ Portée de cette mesure : l'échantillon est dominé par `jamais_collecte`
 * (149) et `pdl_hors_service`, et **ne contient aucun compteur en régime
 * `index_monthly`**. Il ne mesure donc PAS le parc entier et ne réfute pas
 * l'observation d'un `null` sur `index_monthly`. **A3 (« l'attendu vient du
 * compteur ») n'est PAS livrée** : le front dégrade correctement (silence), mais
 * la phrase de fraîcheur ne s'affiche aujourd'hui que pour une minorité du parc.
 *
 * ## Ce qu'on n'a PAS le droit de dire (A4)
 *
 * Échéance dépassée ⇒ on dit qu'il y a du retard, POINT. Aucun détail sur notre
 * système, aucune cause interne, aucune nouvelle date : le signal remonte en
 * interne pour traitement, il ne se raconte pas au client.
 */

import { WorkflowEnedisStreamEnRetard, WorkflowEnedisVerdict } from "@types"

/**
 * Plafond AFFICHÉ de la mise à disposition = l'engagement Enedis (A2), en heures.
 *
 * ⚠️ C'est l'ENGAGEMENT contractuel Enedis, PAS la durée observée. On annonce
 * « quelques heures » et on plafonne à l'engagement : promettre la médiane
 * serait promettre une date qu'on ne tient pas dans la queue de distribution.
 *
 * (Une version antérieure de ce commentaire citait « p50 0,33-0,90 h du 14 au
 * 25/08 sur ~120 000 demandes, max 1,84 h ». Ces chiffres n'ont pas été
 * re-mesurés ici et ne portent aucune décision de ce module — le plafond affiché
 * ne dépend que de l'engagement. [NON VÉRIFIÉ] donc retirés plutôt que redatés.)
 */
export const PLAFOND_ENEDIS_HEURES = 72

/** Libellés qui AFFIRMENT qu'une récupération a été demandée (cf. api-metier). */
const LIBELLES_AVEC_DEMANDE = new Set(["update_once", "historique_onboarding"])

/** Libellé « en retard, mais dans la fenêtre attendue pour ce compteur ». */
const LIBELLE_ATTENTE_ENEDIS = "historique_attente_enedis"

/** Libellé « le retard dépasse nettement l'attendu du flux ». */
const LIBELLE_RETARD_CONFIRME = "historique_retard_confirme"

/**
 * Le trou CONNU chez Enedis (DEV-1650, décision de Mathieu du 07/09). Déclaré ici pour
 * la seule raison qui vaut : sa copie dit « Nous ne les redemandons pas », et une
 * phrase d'avancement la contredirait (cf. `CLES_SANS_AVANCEMENT`).
 */
const LIBELLE_ABSENCE_INSTRUITE = "historique_absence_instruite"

/**
 * Libellé « un flux DÛ manque alors que le reste du compteur arrive » (DEV-1581,
 * décision A11 ; chaîne cliente rouverte par DEV-1632). Émis par api-metier
 * `workflow.enedis.verdict.js#LIBELLE_STREAM_EN_RETARD` — contrat de fil, valeur exacte.
 */
export const LIBELLE_STREAM_EN_RETARD = "historique_stream_en_retard"

/**
 * Variante servie quand AUCUN des flux en retard n'a jamais été reçu : la copie dit
 * alors « ne nous parvient pas », pas « ne nous parvient plus ». api-metier n'émet que
 * la clé nue ; c'est le front qui choisit la variante, sur les dates qu'il a en main.
 */
export const LIBELLE_STREAM_EN_RETARD_JAMAIS_RECU =
    "historique_stream_en_retard_jamais_recu"

/**
 * Le retard de commande QUI EST LE NÔTRE (DEV-1661) : tous les flux dus en retard du
 * compteur sont des commandes M023 que le budget d'affaires ou le planificateur n'a pas
 * repassées. Émis par api-metier `workflow.enedis.verdict.js#LIBELLE_COMMANDE_NON_PASSEE`
 * — contrat de fil, valeur exacte. Le front choisit LUI-MÊME la variante `_sans_donnee`
 * sur `last_data_date` (cf. `SCISSION_PAR_DONNEE` dans le composable).
 */
export const LIBELLE_COMMANDE_NON_PASSEE = "historique_commande_non_passee"
export const LIBELLE_COMMANDE_NON_PASSEE_SANS_DONNEE =
    "historique_commande_non_passee_sans_donnee"

/**
 * Le nom CLIENT de chaque flux servi par enedis (`<sens>:<mesure>`,
 * `registry.stream_key`). Un flux inconnu de cette table est nommé par sa clé brute :
 * dire la clé vaut mieux que taire un flux qui manque.
 */
const NOM_CLIENT_DU_STREAM: Record<string, string> = {
    "soutirage:cdc": "votre courbe de charge",
    "soutirage:energie": "vos énergies journalières",
    "soutirage:energie_horaire": "vos énergies horaires",
    "soutirage:pmax": "votre puissance maximale quotidienne",
    "injection:cdc": "votre courbe de charge d'injection",
    "injection:energie": "vos énergies journalières injectées",
    "injection:energie_horaire": "vos énergies horaires injectées",
    "injection:pmax": "votre puissance maximale quotidienne injectée",
}

/**
 * Nom client d'un flux.
 * @param stream - Clé `<sens>:<mesure>` servie par enedis
 * @returns Le nom en clair, ou la clé brute si elle est inconnue
 */
export function nomClientDuStream(stream: string | null | undefined): string {
    if (!stream) return "une donnée attendue"
    return NOM_CLIENT_DU_STREAM[stream] ?? stream
}

/** Un flux dû en retard, prêt pour la copie : son nom client et sa dernière réception. */
export type FluxEnRetard = {
    nom: string
    /** Dernière date où ce flux a été reçu (ISO jour), ou `null` = jamais reçu. */
    depuis: string | null
}

/**
 * Les flux DUS en retard, nommés (DEV-1581, A11).
 *
 * L'ordre servi par enedis est STABLE (`_ORDRE_STREAMS`) et conservé : la copie doit
 * lister les flux dans le même ordre d'un affichage à l'autre. Un flux sans clé
 * lisible est gardé, pas jeté — il manque quand même.
 *
 * @param streams - `verdict.streams_en_retard` servi par api-metier
 * @returns La liste, vide si enedis n'a rien servi (juge désarmé, ou non jugé)
 */
export function fluxEnRetard(
    streams: WorkflowEnedisStreamEnRetard[] | null | undefined
): FluxEnRetard[] {
    if (!Array.isArray(streams)) return []
    return streams.map((s) => ({
        nom: nomClientDuStream(s?.stream),
        depuis: s?.depuis ?? null,
    }))
}

/**
 * Signature de GROUPEMENT des flux d'un compteur.
 *
 * Même défaut que celui de l'échéance et de la dernière mesure (cf. le composable) :
 * deux compteurs dont des flux DIFFÉRENTS manquent, ou dont le même flux s'est tu à des
 * dates différentes, ne peuvent pas partager une bannière qui NOMME un flux et CITE une
 * date — l'un des deux y lirait un fait faux. Une copie qui cite un fait se groupe sur
 * ce fait.
 *
 * @param streams - `verdict.streams_en_retard` servi par api-metier
 * @returns Une chaîne stable, vide quand aucun flux n'est servi
 */
export function signatureDesFlux(
    streams: WorkflowEnedisStreamEnRetard[] | null | undefined
): string {
    return fluxEnRetard(streams)
        .map((f) => `${f.nom}@${f.depuis ?? "jamais"}`)
        .join(";")
}

/**
 * Un flux dû dont la commande n'est pas repartie de notre côté, prêt pour la copie
 * (DEV-1661) : son nom client, sa dernière réception et la date de notre DERNIÈRE
 * demande.
 */
export type FluxCommandeNonPassee = FluxEnRetard & {
    /**
     * Date d'émission de notre dernière demande pour ce flux (ISO), ou `null` = aucune
     * servie (jamais commandé, ou api-metier / enedis antérieurs au relais). La copie
     * ne date alors rien.
     */
    derniereDemandeLe: string | null
}

/**
 * Les flux dus en retard PAR NOUS, nommés et datés (DEV-1661).
 *
 * Même ordre stable qu'enedis, même tolérance qu'`fluxEnRetard` : un flux sans clé
 * lisible est gardé. `derniere_commande.commanded_at` est le SEUL champ que la copie
 * cite (statut et fenêtre commandée sont des faits de registre, pas de client).
 *
 * @param streams - `verdict.streams_en_retard` servi par api-metier
 * @returns La liste, vide si rien n'est servi
 */
export function fluxCommandesNonPassees(
    streams: WorkflowEnedisStreamEnRetard[] | null | undefined
): FluxCommandeNonPassee[] {
    if (!Array.isArray(streams)) return []
    return streams.map((s) => ({
        nom: nomClientDuStream(s?.stream),
        depuis: s?.depuis ?? null,
        derniereDemandeLe: s?.derniere_commande?.commanded_at ?? null,
    }))
}

/**
 * Signature de GROUPEMENT des commandes non passées d'un compteur (DEV-1661).
 *
 * La copie cite trois faits par flux (nom, dernière réception, dernière demande) :
 * deux compteurs qui n'ont pas les mêmes ne partagent pas une bannière qui les cite.
 *
 * @param streams - `verdict.streams_en_retard` servi par api-metier
 * @returns Une chaîne stable, vide quand aucun flux n'est servi
 */
export function signatureDesCommandes(
    streams: WorkflowEnedisStreamEnRetard[] | null | undefined
): string {
    return fluxCommandesNonPassees(streams)
        .map(
            (f) =>
                `${f.nom}@${f.depuis ?? "jamais"}@${f.derniereDemandeLe ?? "aucune"}`
        )
        .join(";")
}

/**
 * Libellés dont la COPIE DE BASE porte déjà le fait que l'avancement répéterait.
 *
 * ⚠️ Le défaut corrigé (DEV-1624, dossier C2 §2.1) : `historique_retard_confirme` se
 * voyait concaténer `avancement_derniere_mesure` (« Dernière mesure reçue : le …, il y
 * a … jours ») alors que sa copie réécrite CITE elle-même cette date. Le client lisait
 * la même date deux fois dans la même bannière, la seconde fois avec un âge en jours
 * qui n'ajoutait rien à ce que la première venait de dire.
 *
 * La règle est générale, pas un cas particulier : un libellé qui dit un fait dans sa
 * copie ne se le fait pas répéter par sa phrase d'avancement. Tout nouveau libellé qui
 * cite `{derniere_mesure}` s'ajoute ici, une ligne — le pendant côté composable est
 * `CLES_QUI_CITENT_LA_DERNIERE_MESURE`.
 */
export const CLES_SANS_AVANCEMENT = new Set<string>([
    LIBELLE_RETARD_CONFIRME,
    // DEV-1632 : la copie A11 nomme ET date chaque flux elle-même. Une phrase
    // d'avancement y ajouterait une seconde date, celle de la MEILLEURE mesure du
    // compteur, qui est fraîche — le client lirait « en retard » et « reçue hier »
    // dans la même bannière, sur deux objets différents et sans le dire.
    LIBELLE_STREAM_EN_RETARD,
    LIBELLE_STREAM_EN_RETARD_JAMAIS_RECU,
    // DEV-1650 : ici ce n'est pas une RÉPÉTITION qu'on évite, c'est une
    // CONTRADICTION. La copie du trou connu dit « Nous ne les redemandons pas » ;
    // `avancement_eta` annoncerait juste en dessous « Demande envoyée à Enedis le …,
    // les données arrivent le plus souvent en quelques heures » (une commande peut
    // courir sur un AUTRE stream du même compteur), et `avancement_prochaine_mesure`
    // annoncerait une échéance sur une période dont nous venons d'écrire qu'elle est
    // vide. La règle s'élargit donc d'un cran, et son nom la porte déjà : un libellé
    // ne se fait pas ajouter une phrase que sa copie dément.
    LIBELLE_ABSENCE_INSTRUITE,
    // DEV-1661 : même contradiction. La copie dit « n'a pas encore été passée à
    // Enedis » ; `avancement_eta` écrirait « Demande envoyée à Enedis le … » juste en
    // dessous (une commande peut courir sur un AUTRE flux du même compteur), et
    // `avancement_prochaine_mesure` promettrait une échéance que rien ne porte tant
    // que la commande n'est pas partie. La copie date elle-même la dernière réception
    // de chaque flux.
    LIBELLE_COMMANDE_NON_PASSEE,
])

const MS_PAR_HEURE = 3_600_000
const MS_PAR_JOUR = 86_400_000

/**
 * Phrase d'avancement à ajouter sous la copie de bannière.
 *
 * `key` est une clé i18n locale au composable ; les dates sont rendues brutes
 * (le formatage appartient à la couche d'affichage, pas à la décision).
 */
export type AvancementEnedis =
    /** Une demande court, l'échéance annoncée n'est pas passée (A1 + A2). */
    | { key: "avancement_eta"; emisLe: Date; echeance: Date }
    /** L'échéance annoncée est passée : on dit le retard, sans détail (A1 + A4). */
    | { key: "avancement_eta_depassee"; emisLe: Date }
    /** Attente normale : on annonce quand la prochaine mesure est attendue. */
    | {
          key: "avancement_prochaine_mesure"
          derniereMesure: Date
          echeance: Date
      }
    /** Le fait seul — dernière mesure et son âge. Aucune date promise. */
    | {
          key: "avancement_derniere_mesure"
          derniereMesure: Date
          lagDays: number
      }

/**
 * Parse une date servie par l'API (ISO complet ou `YYYY-MM-DD`).
 * @param valeur - Valeur brute
 * @returns Date valide, ou `null` (jamais une date fabriquée)
 */
function parseDateServie(
    valeur: string | Date | null | undefined
): Date | null {
    if (!valeur) return null
    const date = valeur instanceof Date ? valeur : new Date(valeur)
    return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Jour civil UTC d'une date, à minuit — pour comparer des échéances exprimées
 * en JOURS (fraîcheur) sans qu'une heure d'affichage bascule le verdict.
 * @param date - Date à tronquer
 * @returns Date au jour, minuit UTC
 */
function auJour(date: Date): Date {
    return new Date(
        Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
    )
}

/**
 * ETA de MISE À DISPOSITION d'une récupération commandée (A1 + A2).
 *
 * L'échéance ne dépend QUE de la date d'émission : elle est gelée par
 * construction, et l'appel suivant rendra exactement la même.
 *
 * @param emisLe - Horodatage de la commande en vol (`inflight_since`, servi par
 *   dapi en `informations.pdl.data_retrieval.historical.created_at`)
 * @param maintenant - Instant de l'affichage
 * @returns `{emisLe, echeance, depassee}` ou `null` si aucune émission connue
 */
export function etaMiseADisposition(
    emisLe: string | Date | null | undefined,
    maintenant: Date
): { emisLe: Date; echeance: Date; depassee: boolean } | null {
    const emission = parseDateServie(emisLe)
    if (!emission) return null
    const echeance = new Date(
        emission.getTime() + PLAFOND_ENEDIS_HEURES * MS_PAR_HEURE
    )
    return {
        emisLe: emission,
        echeance,
        depassee: maintenant.getTime() > echeance.getTime(),
    }
}

/**
 * Échéance de la PROCHAINE MESURE, sur l'axe fraîcheur (A1 appliqué au flux).
 *
 * `last_data_date + expected_lag_days` : les deux termes sont servis par le
 * verdict, l'attendu est celui DE CE COMPTEUR. Aucun seuil n'est fabriqué ici —
 * un `expected_lag_days` absent rend `null`.
 *
 * @param trace - Traçabilité du verdict embarquée par api-metier
 * @param maintenant - Instant de l'affichage
 * @returns `{derniereMesure, echeance, depassee}` ou `null` si un terme manque
 */
export function echeanceProchaineMesure(
    trace: WorkflowEnedisVerdict | null | undefined,
    maintenant: Date
): { derniereMesure: Date; echeance: Date; depassee: boolean } | null {
    const derniereMesure = parseDateServie(trace?.last_data_date)
    const attendu = trace?.expected_lag_days
    if (!derniereMesure || !Number.isFinite(attendu)) return null
    const echeance = new Date(
        auJour(derniereMesure).getTime() + (attendu as number) * MS_PAR_JOUR
    )
    return {
        derniereMesure,
        echeance,
        depassee: auJour(maintenant).getTime() > echeance.getTime(),
    }
}

/**
 * Phrase d'avancement pour UN compteur, ou `null` quand aucun fait ne la
 * soutient (la bannière garde alors sa copie d'aujourd'hui, inchangée).
 *
 * @param name - Libellé de workflow servi par api-metier
 * @param trace - Traçabilité du verdict (`element.verdict`)
 * @param emisLe - Horodatage de la commande en vol, ou `null`
 * @param maintenant - Instant de l'affichage
 * @returns Phrase d'avancement, ou `null`
 */
export function avancementEnedis(
    name: string,
    trace: WorkflowEnedisVerdict | null | undefined,
    emisLe: string | Date | null | undefined,
    maintenant: Date
): AvancementEnedis | null {
    // Le fait est déjà dans la copie de base : on ne le sert pas deux fois (DEV-1624).
    if (CLES_SANS_AVANCEMENT.has(name)) return null

    if (LIBELLES_AVEC_DEMANDE.has(name)) {
        // ⚠️ Ces deux libellés AFFIRMENT déjà « une récupération a été
        // demandée ». On ne date cette affirmation que si une commande est
        // vraiment en vol ET qu'on connaît son heure d'émission : sinon,
        // annoncer une échéance serait la datation à la louche que DEV-1489 a
        // précisément retirée.
        if ((trace?.inflight_n ?? 0) <= 0) return null
        const eta = etaMiseADisposition(emisLe, maintenant)
        if (!eta) return null
        return eta.depassee
            ? { key: "avancement_eta_depassee", emisLe: eta.emisLe }
            : {
                  key: "avancement_eta",
                  emisLe: eta.emisLe,
                  echeance: eta.echeance,
              }
    }

    if (name === LIBELLE_ATTENTE_ENEDIS) {
        const prochaine = echeanceProchaineMesure(trace, maintenant)
        if (!prochaine) return null
        // Échéance déjà passée alors que le backend juge encore l'attente
        // normale (son seuil de retard confirmé est un MULTIPLE de l'attendu) :
        // on ne re-promet pas une date qu'on vient de manquer, on dit le fait.
        if (prochaine.depassee) return faitDerniereMesure(trace)
        return {
            key: "avancement_prochaine_mesure",
            derniereMesure: prochaine.derniereMesure,
            echeance: prochaine.echeance,
        }
    }

    // ⚠️ `historique_retard_confirme` rendait ici `faitDerniereMesure(trace)`. La
    // branche est retirée par `CLES_SANS_AVANCEMENT` au-dessus, pas par une exception
    // locale : sa copie réécrite (DEV-1624) porte désormais la date elle-même, et il
    // n'a jamais été question d'y annoncer une échéance (le backend a conclu que le
    // flux a décroché, et A4 interdit d'expliquer notre système).
    return null
}

/**
 * Le FAIT nu : dernière mesure reçue et son âge. Rendu seulement si les deux
 * sont servis (ils le sont ou ne le sont pas ensemble : `lag_days` est `null`
 * exactement quand aucune donnée n'a jamais été reçue).
 *
 * @param trace - Traçabilité du verdict
 * @returns Phrase de fait, ou `null`
 */
function faitDerniereMesure(
    trace: WorkflowEnedisVerdict | null | undefined
): AvancementEnedis | null {
    const derniereMesure = parseDateServie(trace?.last_data_date)
    if (!derniereMesure || !Number.isFinite(trace?.lag_days)) return null
    return {
        key: "avancement_derniere_mesure",
        derniereMesure,
        lagDays: trace?.lag_days as number,
    }
}
